<script setup lang="ts">
import type { TermType, TermPayloadType } from "@/common/types/term/term";

import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "validate"]);

const props = defineProps<{
	data: TermType | null;
	isPatch?: boolean;
	isEdit?: boolean;
}>();

const DEFAULT_DATA = {
	code: "",
	name: "",
	status: "ACTIVE",
	description: null,
};

const payload = ref<TermPayloadType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

const isValid = ref(false);

const optionsStatus = computed(() => [
	{
		option: t("common.labels.active"),
		value: "ACTIVE",
	},
	{
		option: t("common.labels.inactive"),
		value: "INACTIVE",
	},
]);

function validate() {
	let free = ["description", "metadata"];

	Object.keys(payload.value).forEach((key) => {
		delete errors.value[key];

		if (
			(payload.value[key] === null ||
				payload.value[key] === "" ||
				payload.value[key] === undefined) &&
			!free.includes(key)
		)
			errors.value[key] = t("error.required");
	});

	isValid.value = Object.keys(errors.value).length === 0;
}

function update(key: keyof typeof payload.value) {
	validate();

	if (props.isPatch) {
		if (payload.value[key] === props.data[key]) return;

		emit("update:data", { [key]: payload.value[key] });
		return;
	}

	emit("update:data", payload.value);

	setTimeout(() => emit("validate", isValid.value), 50);
}

function resumeData() {
	if (!props.data) return;

	const { code, name, description, status } = props.data;

	payload.value = {
		...payload.value,
		code,
		name,
		description,
		status,
	};
}

function maskCode(value: string) {
	return value?.replaceAll(" ", "-")?.toUpperCase();
}

const debouceUpdate = debounce(update, 300);

watch(
	() => props.data,
	() => {
		resumeData();
	},
	{ immediate: true },
);
</script>

<template>
	<article class="form-data">
		<base-input
			v-model="payload.name"
			:value="payload.name"
			:placeholder="t('common.placeholders.name')"
			required
			maxlength="150"
			@blur="debouceUpdate('name')"
		>
			<template #label> {{ t("common.labels.name") }} *</template>
			<template v-if="errors.name" #error> {{ errors.name }} </template>
		</base-input>

		<div class="form-group">
			<base-input
				v-model="payload.code"
				:value="payload.code"
				:placeholder="t('common.placeholders.code')"
				:mask="maskCode"
				required
				maxlength="50"
				@blur="debouceUpdate('code')"
			>
				<template #label> {{ t("common.labels.code") }} *</template>
				<template v-if="errors.code" #error> {{ errors.code }} </template>
			</base-input>

			<base-select
				v-model="payload.status"
				:value="payload.status"
				:options="optionsStatus"
				:is-empty="false"
				@change="debouceUpdate('status')"
			>
				<template #label> {{ t("common.labels.status") }}</template>
			</base-select>
		</div>

		<base-text-editor
			v-model="payload.description"
			@blur="debouceUpdate('description')"
		>
			<template #label> {{ t("category.labels.description") }} *</template>
		</base-text-editor>
	</article>
</template>

<style lang="scss" scoped>
.form-data {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem;

	.form-group {
		display: flex;
		flex-direction: row;
		gap: 1rem;
	}
}

@media (max-width: 1024px) {
	.form-data {
		.form-group {
			flex-direction: column;
		}
	}
}
</style>
